import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Typography,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { login, signup, forgot_password } from "../../services/apis";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../redux/reducer";

const inputSx = {
  "& label": { color: "#BBBBBB", fontSize: 15 },
  "& label.Mui-focused": { color: "#00A9CE" },
  "& .MuiInput-underline:after": { borderBottomColor: "#00A9CE" },
};

const Login = () => {
  const [eyeOpen, setEyeOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPass, setIsForgotPass] = useState(false);
  const [isLoging, setIsLoging] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cookies = new Cookies();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const images = [
    "/images/HtsLogin1.svg",
    "/images/HtsLogin2.svg",
    "/images/HtsLogin3.svg",
    "/images/HtsLogin4.svg",
  ];

  useEffect(() => {
    cookies.remove("hts-role");
    cookies.remove("hts-tok");
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setIndex((prev) => (prev + 1) % images.length);
        setFadeIn(true);
      }, 800);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Login
  const loginFormik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema: Yup.object({
      username: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      password: Yup.string().required("Password is required"),
    }),
    onSubmit: async (values) => {
      setIsLoging(true);
      try {
        const response = await login(values);
        if (response.data.statuscode === 1) {
          const { access_token, role } = response.data.data;
          cookies.set("hts-tok", access_token, {
            maxAge: 86400,
            secure: true,
            sameSite: "strict",
          });
          cookies.set("hts-role", role, {
            maxAge: 86400,
            secure: true,
            sameSite: "strict",
          });
          dispatch(setCredentials({ role }));
          navigate("/home");
        } else {
          toast.error(response.data.Message);
        }
      } catch (err) {
        console.error(err);
        toast.error("Login failed.");
      }
      setIsLoging(false);
    },
  });

  // Forgot Password
  const forgotPasswordFormik = useFormik({
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email").required("Email is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      console.log("val--", values);

      try {
        const response = await forgot_password(values.email);
        if (response.data.statuscode === 1) {
          toast.success("Password reset link sent.");
          setIsForgotPass(false);
        } else {
          toast.error(response.data.Message || "Failed to send reset link.");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error occurred.");
      }
      setIsSubmitting(false);
    },
  });

  // Signup
  const signupFormik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      phoneno: "",
      address: "",
      city: "",
      pincode: "",
      state: "",
      country: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("First name required"),
      last_name: Yup.string().required("Last name required"),
      email: Yup.string().email("Invalid email").required("Email required"),
      password: Yup.string().required("Password required"),
      phoneno: Yup.string().required("Phone required"),
      address: Yup.string().required("Address required"),
      city: Yup.string().required("City required"),
      pincode: Yup.string().required("Pincode required"),
      state: Yup.string().required("State required"),
      country: Yup.string().required("Country required"),
    }),
    onSubmit: async (values) => {
      setIsSigning(true);
      const { address, city, ...rest } = values;
      const apiBody = { ...rest, street: address, area: city };
      try {
        const response = await signup(apiBody);
        if (response.data.statuscode === 1) {
          toast.success(response.data.Message);
          setIsLogin(true);
        } else {
          toast.error(response.data.Message);
        }
      } catch (err) {
        console.error(err);
        toast.error("Signup failed.");
      }
      setIsSigning(false);
    },
  });

  const renderField = (id, label, formik, type = "text") => (
    <FormControl variant="standard" fullWidth sx={inputSx}>
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <Input
        id={id}
        type={id === "password" ? (eyeOpen ? "text" : "password") : type}
        {...formik.getFieldProps(id)}
        error={formik.touched[id] && Boolean(formik.errors[id])}
        endAdornment={
          id === "password" && (
            <InputAdornment position="end">
              {eyeOpen ? (
                <VisibilityOutlinedIcon
                  onClick={() => setEyeOpen(false)}
                  sx={{ cursor: "pointer" }}
                />
              ) : (
                <VisibilityOffOutlinedIcon
                  onClick={() => setEyeOpen(true)}
                  sx={{ cursor: "pointer" }}
                />
              )}
            </InputAdornment>
          )
        }
      />
      {formik.touched[id] && formik.errors[id] && (
        <Typography sx={{ fontSize: 12, color: "red", mt: 0.5 }}>
          {formik.errors[id]}
        </Typography>
      )}
    </FormControl>
  );

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f5f5f5",
      }}
    >
      {/* Rotated Blue Background */}
      <Box
        sx={{
          width: { xs: 500, lg: 580, xl: 690 },
          height: { xs: 500, lg: 580, xl: 670 },
          backgroundColor: "#00A9CE",
          borderRadius: 20,
          transform: "rotate(35deg)",
          position: "absolute",
          top: { xs: -200, lg: -280 },
          left: { xs: -80, lg: 20 },
        }}
      />

      <Box
        sx={{
          width: { xs: "90%", sm: "600px", md: "700px", xl: "900px" },
          boxShadow: 3,
          borderRadius: 8,
          backgroundColor: "white",
          display: "flex",
          zIndex: 1,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: isLogin ? "50%" : "100%" },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "auto",
          }}
        >
          <AnimatePresence mode="wait">
            {isLogin ? (
              <motion.form
                key={isForgotPass ? "forgot" : "login"}
                onSubmit={
                  isForgotPass
                    ? forgotPasswordFormik.handleSubmit
                    : loginFormik.handleSubmit
                }
                initial={{ x: isForgotPass ? 300 : 0, opacity: 0 }}
                animate={{ x: 1, opacity: 1 }}
                exit={{ x: isForgotPass ? 0 : 0, opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  alignItems: "center",
                  padding: 32,
                  width: "80%",
                  // minHeight: "505px",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/Logo.svg"
                  alt="Logo"
                  style={{ height: 30, marginBottom: 16 }}
                />

                {isForgotPass ? (
                  renderField("email", "Email", forgotPasswordFormik)
                ) : (
                  <>
                    {renderField("username", "Email", loginFormik)}
                    {renderField("password", "Password", loginFormik)}
                  </>
                )}

                {!isForgotPass && (
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "#00A9CE",
                      cursor: "pointer",
                      alignSelf: "flex-end",
                    }}
                    onClick={() => {
                      loginFormik.resetForm();
                      setIsForgotPass(true);
                    }}
                  >
                    Forgot Password?
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    background: "#00A9CE",
                    color: "white",
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 12,
                  }}
                >
                  {isLoging || isSubmitting ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : isForgotPass ? (
                    "Send password reset email"
                  ) : (
                    "Login"
                  )}
                </Button>

                {isForgotPass ? (
                  <Typography sx={{ fontSize: 14 }}>
                    Remember your password?{" "}
                    <span
                      onClick={() => {
                        forgotPasswordFormik.resetForm();
                        setIsForgotPass(false);
                      }}
                      style={{ color: "#00A9CE", cursor: "pointer" }}
                    >
                      Login
                    </span>
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 14 }}>
                    Don’t have an account?{" "}
                    <span
                      onClick={() => {
                        signupFormik.resetForm();
                        setIsLogin(false);
                      }}
                      style={{ color: "#00A9CE", cursor: "pointer" }}
                    >
                      Sign Up
                    </span>
                  </Typography>
                )}
              </motion.form>
            ) : (
              <motion.form
                key="signup"
                onSubmit={signupFormik.handleSubmit}
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 600, opacity: 0 }}
                transition={{ duration: 0.5 }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 16,
                  alignItems: "center",
                  padding: 32,
                  width: "80%",
                  overflow: "auto",
                  // minHeight: "505px",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/Logo.svg"
                  alt="Logo"
                  style={{ height: 35, marginBottom: 16 }}
                />

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  {[
                    "first_name",
                    "last_name",
                    "email",
                    "password",
                    "phoneno",
                    "address",
                    "city",
                    "pincode",
                    "state",
                    "country",
                  ].map((field) => (
                    <Box key={field} sx={{ width: { xs: "100%", sm: "48%" } }}>
                      {renderField(
                        field,
                        field
                          .replace("_", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()),
                        signupFormik
                      )}
                    </Box>
                  ))}
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    background: "#00A9CE",
                    color: "white",
                    px: 4,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 12,
                    mt: 2,
                  }}
                >
                  {isSigning ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : (
                    "Sign Up"
                  )}
                </Button>

                <Typography sx={{ fontSize: 14 }}>
                  Already have an account?{" "}
                  <span
                    onClick={() => {
                      loginFormik.resetForm();
                      setIsLogin(true);
                    }}
                    style={{ color: "#00A9CE", cursor: "pointer" }}
                  >
                    Login
                  </span>
                </Typography>
              </motion.form>
            )}
          </AnimatePresence>
        </Box>

        {/* Right Side Image */}
        {isLogin && (
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              width: "50%",
              borderTopRightRadius: 32,
              borderBottomRightRadius: 32,
            }}
          >
            <Fade in={fadeIn} timeout={2000}>
              <Box
                component="img"
                src={images[index]}
                alt={`Image ${index + 1}`}
                sx={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  borderTopRightRadius: 32,
                  borderBottomRightRadius: 32,
                }}
              />
            </Fade>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Login;
